class CompleteRitual extends HTMLElement {
  constructor() {
    super()

    this.onClick = this.onClick.bind(this)
    this.onChange = this.onChange.bind(this)
    this.onDocumentClick = this.onDocumentClick.bind(this)
  }

  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}'
    this.grid = this.querySelector('.complete-ritual__grid')
    this.bar = this.querySelector('[data-cr-bar]')
    this.totalEl = this.querySelector('[data-cr-total]')
    this.submitButton = this.querySelector('[data-cr-submit]')
    this.spinner = this.submitButton && this.submitButton.querySelector('.loading-overlay__spinner')

    this.addEventListener('click', this.onClick)
    this.addEventListener('change', this.onChange)
    document.addEventListener('click', this.onDocumentClick)

    if (this.submitButton) this.submitButton.addEventListener('click', () => this.addToCart())

    this.updateBar()
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick)
  }

  onClick(event) {
    const option = event.target.closest('[data-cr-handle]')
    if (option) {
      event.preventDefault()
      this.selectShade(option)
      return
    }

    const toggle = event.target.closest('[data-cr-select-toggle]')
    if (toggle) {
      this.toggleSelect(toggle.closest('[data-cr-select]'))
      return
    }

    // Clicks inside an open list should not toggle the tile.
    if (event.target.closest('[data-cr-select]')) return
    // The native checkbox handles its own toggling.
    if (event.target.closest('[data-cr-checkbox]')) return

    const tile = event.target.closest('[data-cr-tile]')
    if (tile) this.toggleTile(tile)
  }

  onChange(event) {
    if (!event.target.matches('[data-cr-checkbox]')) return
    const wrap = event.target.closest('[data-cr-tile-wrap]')
    if (wrap) wrap.classList.toggle('complete-ritual__tile-wrap--selected', event.target.checked)
    this.updateBar()
  }

  onDocumentClick(event) {
    if (event.target.closest('[data-cr-select]')) return
    this.closeAllSelects()
  }

  toggleTile(tile) {
    const checkbox = tile.querySelector('[data-cr-checkbox]')
    if (!checkbox) return
    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event('change', { bubbles: true }))
  }

  toggleSelect(select) {
    if (!select) return
    const willOpen = !select.classList.contains('complete-ritual__select--opened')
    this.closeAllSelects()
    select.classList.toggle('complete-ritual__select--opened', willOpen)
    const toggle = select.querySelector('[data-cr-select-toggle]')
    if (toggle) toggle.setAttribute('aria-expanded', String(willOpen))
  }

  closeAllSelects() {
    this.querySelectorAll('.complete-ritual__select--opened').forEach((select) => {
      select.classList.remove('complete-ritual__select--opened')
      const toggle = select.querySelector('[data-cr-select-toggle]')
      if (toggle) toggle.setAttribute('aria-expanded', 'false')
    })
  }

  selectShade(option) {
    const wrap = option.closest('[data-cr-tile-wrap]')
    const tile = option.closest('[data-cr-tile]')
    const handle = option.dataset.crHandle
    if (!wrap || !tile || !handle) return

    if (tile.dataset.handle === handle) {
      this.closeAllSelects()
      return
    }

    tile.classList.add('complete-ritual__tile--loading')

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'

    fetch(`${root}products/${handle}?section_id=complete-ritual-tile`)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html')
        const newWrap = doc.querySelector('[data-cr-tile-wrap]')
        if (!newWrap) {
          tile.classList.remove('complete-ritual__tile--loading')
          return
        }
        wrap.replaceWith(newWrap)
        this.updateBar()
      })
      .catch((error) => {
        console.warn('Complete Ritual: could not load product tile.', error)
        tile.classList.remove('complete-ritual__tile--loading')
      })
  }

  getCheckedTiles() {
    return Array.from(this.querySelectorAll('[data-cr-checkbox]:checked')).map((checkbox) =>
      checkbox.closest('[data-cr-tile]')
    )
  }

  updateBar() {
    if (!this.bar) return
    const tiles = this.getCheckedTiles()

    if (!tiles.length) {
      this.bar.hidden = true
      return
    }

    const total = tiles.reduce((sum, tile) => sum + parseInt(tile.dataset.price || '0', 10), 0)
    if (this.totalEl) this.totalEl.textContent = this.formatMoney(total)
    this.bar.hidden = false
  }

  addToCart() {
    const tiles = this.getCheckedTiles()
    if (!tiles.length) return

    const items = tiles
      .map((tile) => tile.dataset.variantId)
      .filter(Boolean)
      .map((id) => ({ id, quantity: 1 }))

    if (!items.length) return

    this.setLoading(true)

    const cart = document.querySelector('cart-drawer')
    const body = { items }

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id)
      body.sections_url = window.location.pathname
    }

    fetch(`${window.routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.warn('Complete Ritual: add to cart failed.', response)
          return
        }

        this.resetSelection()

        if (cart && typeof cart.renderContents === 'function') {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty')
          cart.renderContents(response)
        }
      })
      .catch((error) => {
        console.warn('Complete Ritual: add to cart request failed.', error)
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  resetSelection() {
    this.querySelectorAll('[data-cr-checkbox]:checked').forEach((checkbox) => {
      checkbox.checked = false
      const wrap = checkbox.closest('[data-cr-tile-wrap]')
      if (wrap) wrap.classList.remove('complete-ritual__tile-wrap--selected')
    })
    if (this.bar) this.bar.hidden = true
  }

  setLoading(isLoading) {
    if (!this.submitButton) return
    this.submitButton.classList.toggle('loading', isLoading)
    this.submitButton.setAttribute('aria-disabled', String(isLoading))
    if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading)
  }

  formatMoney(cents) {
    const value = typeof cents === 'string' ? cents.replace('.', '') : cents
    const format = this.moneyFormat

    const formatWithDelimiters = (number, precision = 2, thousands = ' ', decimal = ',') => {
      if (isNaN(number) || number == null) return 0

      const fixed = (number / 100).toFixed(precision)
      const parts = fixed.split('.')
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`)
      const centsPart = parts[1] ? decimal + parts[1] : ''

      return dollars + centsPart
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, placeholder) => {
      switch (placeholder) {
        case 'amount':
          return formatWithDelimiters(value, 2)
        case 'amount_no_decimals':
          return formatWithDelimiters(value, 0)
        case 'amount_with_comma_separator':
          return formatWithDelimiters(value, 2, '.', ',')
        case 'amount_no_decimals_with_comma_separator':
          return formatWithDelimiters(value, 0, '.', ',')
        case 'amount_with_space_separator':
          return formatWithDelimiters(value, 2, ' ', ',')
        case 'amount_no_decimals_with_space_separator':
          return formatWithDelimiters(value, 0, ' ', '')
        default:
          return formatWithDelimiters(value, 2)
      }
    })
  }
}

customElements.define('complete-ritual', CompleteRitual)
